/**
 * Evaluador de ejercicios: state machine que lee un ExerciseSchema y, frame
 * por frame, emite feedback en tiempo real + detecta repeticiones + genera
 * métricas por rep.
 *
 * No depende de MediaPipe — recibe keypoints como input. Testeable con mocks.
 */

import type {
  EvaluationResult,
  ExerciseRule,
  ExerciseSchema,
  Keypoint,
  KeypointIndex,
  PoseResult,
  RepResult,
  RuleEvaluation,
} from "./types";
import { KP } from "./types";
import {
  calc3DAngle,
  horizontalDistance,
  trunkAngle,
} from "./angles";

// Tolerancias del rep detector. TODO chunk futuro: mover al schema cuando
// agreguemos más ejercicios (algunos pueden tener wrist vertical legítimo o
// pivots distintos). Calibradas al cuerpo de Ale + cámara Mac actual; cuando
// testeemos en celular real probablemente requieran ajuste — eventualmente
// pasa a body-type personalization Nivel 2 (post-beta).

// GHOST ZONES — MVP informal, ver docs/decisions/ghost-zones.md (futuro: formalizar en schema).
// Wrist visibility está crónicamente baja en rotación externa por auto-oclusión
// del antebrazo contra el torso en INICIO. Threshold permisivo (0.05) acepta la
// pose-estimation del modelo aunque tenga baja confianza — tradeoff: el detector
// tolera ruido en posición de wrist a cambio de no bloquear toda la rep.
// Cuando agreguemos otros ejercicios con ghost zones distintas, refactorizamos a
// schema-declared ghost zones.
const MIN_VISIBILITY_PHASE_VERTEX = 0.7; // shoulder — siempre visible, threshold normal
// iter 3 (1-may): 0.30 → 0.20. QA en celu (Eruda) mostró que elbow_v queda crónicamente
// entre 0.27-0.30, descartando frames legítimos por "low visibility". 0.20 deja pasar
// y mantiene calidad razonable.
const MIN_VISIBILITY_PHASE_ELBOW = 0.20;
const MIN_VISIBILITY_PHASE_WRIST = 0.05; // muñeca: ghost zone esperada en INICIO
// iter 3 (1-may): 0.30 → 0.20, alineado con PHASE_ELBOW por la misma razón.
const MIN_VISIBILITY_VALIDATION_ELBOW = 0.20;
const MIN_VISIBILITY_VALIDATION_WRIST = 0.05;
const EMA_ALPHA = 0.3;
const PHASE_TRANSITION_DEBOUNCE_FRAMES = 5;
// iter 5 (4-may): 5 → 7. QA del 4/5 mostró que sway de baja amplitud (3-5°)
// pasaba como falso positivo en el detector — reps "fantasma" con prominencias
// chicas que no eran movimiento real. Reps reales en QA: 6.75-10° promedio,
// holgadas sobre el nuevo threshold de 7°. Ver decisión completa en
// docs/decisions/iter5-prominence-elbow.md.
const MIN_PEAK_PROMINENCE_DEGREES = 7;
const ELBOW_STABILITY_THRESHOLD = 0.15;
const WRIST_VERTICAL_THRESHOLD = 0.15;
// iter 4 (2-may): una regla solo cuenta como violación del rep si falla en
// más del 25% de los frames. Antes (iter 3 y previas) bastaba 1 frame para
// invalidarlo, lo que en QA del 1/5 marcó el 100% de los reps como inválidos
// por fluctuación natural humana en el ángulo del codo. quality sigue siendo
// proporcional sobre TODAS las desviaciones — el threshold solo afecta el
// flag binario `valid` y la lista `violations`.
const VIOLATION_FRAME_PCT_THRESHOLD = 0.25;

/** Versión semántica del evaluator. Se persiste en pose_analysis.detector
 *  al cerrar sesión para trazabilidad: qué versión del state machine /
 *  reglas de tuning procesó qué sesión. Subir cuando cambia la lógica
 *  estructural o se ajustan tolerancias significativamente. */
export const EVALUATOR_VERSION = "iter5";

type RepPhase = "inicio" | "concentrica" | "pico" | "excentrica";

interface SmoothedKeypoints {
  shoulder: Keypoint;
  elbow: Keypoint;
  wrist: Keypoint;
}

interface PhaseEntry {
  phase: RepPhase;
  enteredAt: number;
}

interface RepInProgress {
  startedAt: number;
  /** Ángulo máximo observado. Es el "pico" para direction="increasing"
   *  (rot externa). Para decreasing, sigue trackeándose pero el pico
   *  semántico es angleMin. */
  angleMax: number;
  /** Ángulo mínimo observado. Es el "pico" para direction="decreasing"
   *  (rot interna). Para increasing, sigue trackeándose por simetría
   *  pero no se usa en cálculos. */
  angleMin: number;
  violationsPerFrame: Map<string, number>; // violationId → frames en los que falló
  totalFrames: number;
  trunkAngleMin: number;
  trunkAngleMax: number;
  // Posiciones smoothed al entrar a concentrica — para Fix A/B.
  startPositions: {
    elbow: { x: number; y: number };
    wrist: { x: number; y: number };
  };
  // Ángulo al entrar a concentrica — para peak prominence check.
  angleAtStart: number;
}

export class ExerciseEvaluator {
  private schema: ExerciseSchema;
  private phase: RepPhase = "inicio";
  private phaseEnteredAt = 0;
  private repCount = 0;
  private repInProgress: RepInProgress | null = null;
  // Buffer de keypoints smoothed (EMA) — persiste entre frames y reps.
  // null hasta el primer frame válido (post visibility filter).
  private smoothedKeypoints: SmoothedKeypoints | null = null;
  // Contador de frames consecutivos FUERA de la start zone (en la dirección
  // del movimiento esperado) — para el debounce de entrada a concentrica.
  // Renombrado de "framesAboveLowThreshold" para neutralidad direccional:
  // increasing = above, decreasing = below.
  private framesOutsideStartZone = 0;
  // Valores dev-facing para el DetectorDebugHUD. Se actualizan dentro de
  // updateRepDetector cada frame (o con marker LOW en visibility fail).
  // Sprint 3 (6/8/2026) — permite calibrar thresholds/rangos leyendo el
  // ángulo en vivo sobre la sesión, mismo patrón que NeckLateralFlexionEvaluator.
  private lastDebug: EvaluationResult["debug"] = undefined;

  /** Implementa Evaluator.version (interface común). Hardcoded a la
   *  constante exportada — SesionCamara lee esto en vez de importar la
   *  constante directamente, así cada evaluator declara su propia versión. */
  readonly version = EVALUATOR_VERSION;

  constructor(schema: ExerciseSchema) {
    this.schema = schema;
  }

  /** Resetea el estado — llamar al iniciar una nueva serie. */
  reset(): void {
    this.phase = "inicio";
    this.phaseEnteredAt = 0;
    this.repCount = 0;
    this.repInProgress = null;
    this.smoothedKeypoints = null;
    this.framesOutsideStartZone = 0;
    this.lastDebug = undefined;
  }

  /**
   * Procesar un frame. Devuelve EvaluationResult con el estado actual.
   * Si en este frame se completó una rep, `completedRep` viene poblado.
   */
  evaluate(pose: PoseResult): EvaluationResult {
    const kp = pose.keypoints;
    const now = pose.timestamp;

    // 1) Evaluar reglas del ejercicio sobre este frame
    const ruleEvaluations: RuleEvaluation[] = this.schema.rules.map(
      (rule, idx) => this.evaluateRule(rule, idx, pose),
    );

    // 2) Actualizar estadísticas de la rep en curso
    if (this.repInProgress) {
      this.repInProgress.totalFrames++;
      for (const r of ruleEvaluations) {
        if (!r.passed) {
          const prev =
            this.repInProgress.violationsPerFrame.get(r.violationId) ?? 0;
          this.repInProgress.violationsPerFrame.set(r.violationId, prev + 1);
        }
      }
      // Track trunk angle para regla de trunk_stability
      const tAngle = this.safeTrunkAngle(kp);
      if (tAngle !== null) {
        this.repInProgress.trunkAngleMin = Math.min(
          this.repInProgress.trunkAngleMin,
          tAngle,
        );
        this.repInProgress.trunkAngleMax = Math.max(
          this.repInProgress.trunkAngleMax,
          tAngle,
        );
      }
    }

    // 3) Detector de repeticiones
    const completedRep = this.updateRepDetector(kp, now);

    return {
      ruleEvaluations,
      repCount: this.repCount,
      repPhase: this.phase,
      completedRep,
      debug: this.lastDebug,
    };
  }

  // ==========================================================================
  // Evaluación de reglas
  // ==========================================================================

  private evaluateRule(
    rule: ExerciseRule,
    ruleIndex: number,
    pose: PoseResult,
  ): RuleEvaluation {
    const kp = pose.keypoints;
    switch (rule.type) {
      case "angle_range": {
        const angle = calc3DAngle(kp[rule.a], kp[rule.vertex], kp[rule.b]);
        const passed = angle >= rule.min && angle <= rule.max;
        return {
          ruleIndex,
          violationId: rule.violationId,
          passed,
          feedback: passed ? "" : rule.feedback,
          currentValue: angle,
        };
      }
      case "horizontal_distance_max": {
        const d = horizontalDistance(kp[rule.kpA], kp[rule.kpB]);
        const passed = d <= rule.maxDistance;
        return {
          ruleIndex,
          violationId: rule.violationId,
          passed,
          feedback: passed ? "" : rule.feedback,
          currentValue: d,
        };
      }
      case "trunk_stability": {
        if (!this.repInProgress) {
          return {
            ruleIndex,
            violationId: rule.violationId,
            passed: true,
            feedback: "",
            currentValue: 0,
          };
        }
        const variation =
          this.repInProgress.trunkAngleMax - this.repInProgress.trunkAngleMin;
        const passed = variation <= rule.maxVariationDeg;
        return {
          ruleIndex,
          violationId: rule.violationId,
          passed,
          feedback: passed ? "" : rule.feedback,
          currentValue: variation,
        };
      }
      case "vertical_alignment_max_deg": {
        // Desviación del vector top→bottom respecto a la vertical del
        // frame. Y crece hacia abajo en coords MediaPipe, así que
        // atan2(|dx|, dy) da 0° cuando el vector apunta directamente
        // hacia abajo (plomo) y crece cuando se abre lateralmente.
        // Ver doc del type en types.ts para la corrección clínica de Ale.
        //
        // ASPECT-RATIO FIX: los keypoints x/y son normalizados
        // separadamente por videoWidth/videoHeight. Si vw ≠ vh, un
        // atan2 directo sobre las coords normalizadas distorsiona el
        // ángulo — el umbral 20° sería válido solo para un aspect
        // ratio específico y se rompería entre desktop (landscape) y
        // celular (portrait). Multiplicamos por las dims reales del
        // stream antes del atan2 para obtener el ángulo geométrico en
        // pixels — calibración única, invariante al device.
        // Fallback: si videoWidth/Height es 0 (warm-up) o undefined,
        // usamos normalized directo — comportamiento previo, aceptable
        // para el primer frame.
        const top = kp[rule.kpTop];
        const bottom = kp[rule.kpBottom];
        const vw = pose.videoWidth || 1;
        const vh = pose.videoHeight || 1;
        const dxPx = (bottom.x - top.x) * vw;
        const dyPx = (bottom.y - top.y) * vh;
        const deviation = Math.atan2(Math.abs(dxPx), dyPx) * (180 / Math.PI);
        const passed = deviation <= rule.maxDeviationDeg;
        return {
          ruleIndex,
          violationId: rule.violationId,
          passed,
          feedback: passed ? "" : rule.feedback,
          currentValue: deviation,
        };
      }
    }
  }

  private safeTrunkAngle(kp: Keypoint[]): number | null {
    const ls = kp[KP.LEFT_SHOULDER];
    const rs = kp[KP.RIGHT_SHOULDER];
    const lh = kp[KP.LEFT_HIP];
    const rh = kp[KP.RIGHT_HIP];
    if (!ls || !rs || !lh || !rh) return null;
    if (
      ls.visibility < 0.5 ||
      rs.visibility < 0.5 ||
      lh.visibility < 0.5 ||
      rh.visibility < 0.5
    ) {
      return null;
    }
    return trunkAngle(ls, rs, lh, rh);
  }

  // ==========================================================================
  // Detector de repeticiones (state machine)
  // ==========================================================================

  private updateRepDetector(
    kp: Keypoint[],
    now: number,
  ): RepResult | undefined {
    const det = this.schema.repDetector;
    // det puede ser undefined para schemas video_only (PR1 catálogo
    // expandido). El ExerciseEvaluator no debería instanciarse en ese
    // caso — el branch ocurre en SesionCamara antes — pero guard
    // defensivo evita crash si por algún motivo llega acá.
    if (!det || det.type !== "oscillating_angle") return undefined;

    // Direction helpers (PR2 Sprint 3) — invierten las comparaciones de
    // umbrales para soportar movimientos donde el ángulo DECRECE durante
    // la concéntrica (rotación interna). Default "increasing" preserva
    // comportamiento previo de rot_externa_banda + sentadilla.
    //
    // Convención de thresholds (idéntica para ambas direcciones):
    //   - lowThreshold = el menor de los 2 valores numéricos.
    //   - highThreshold = el mayor.
    // La SEMÁNTICA de cuál es "start" y cuál es "peak" depende de
    // direction:
    //   increasing:  start = bajo lowThreshold,  peak = sobre highThreshold.
    //   decreasing:  start = sobre highThreshold, peak = bajo lowThreshold.
    // Entre los dos thresholds hay una "zona muerta" — no es start ni peak.
    // Esto evita ambigüedad en valores intermedios (45 < a < 55).
    const direction = det.direction ?? "increasing";

    // True si el ángulo está EN la "start zone" (posición inicial del
    // movimiento, antes de la concéntrica).
    const isInStartZone = (a: number): boolean =>
      direction === "decreasing"
        ? a >= det.highThreshold
        : a <= det.lowThreshold;

    // True si el ángulo cruzó AL pico (rep llegó al máximo del movimiento).
    const isInPeakZone = (a: number): boolean =>
      direction === "decreasing"
        ? a <= det.lowThreshold
        : a >= det.highThreshold;

    const rawShoulder = kp[det.vertex];
    const rawElbow = kp[det.a];
    const rawWrist = kp[det.b];

    // T1 — Visibility filter con thresholds distintos por keypoint.
    // Shoulder (vertex/pivote) debe estar siempre visible. Elbow es pivote
    // del miembro que se mueve, threshold normal. Wrist tiene ghost zone
    // esperada en INICIO por auto-oclusión, threshold permisivo.
    if (
      rawShoulder.visibility < MIN_VISIBILITY_PHASE_VERTEX ||
      rawElbow.visibility < MIN_VISIBILITY_PHASE_ELBOW ||
      rawWrist.visibility < MIN_VISIBILITY_PHASE_WRIST
    ) {
      console.warn("[Evaluator] frame skipped: low visibility", {
        shoulder_v: rawShoulder.visibility,
        elbow_v: rawElbow.visibility,
        wrist_v: rawWrist.visibility,
      });
      this.lastDebug = {
        visibility: "LOW",
        sh_v: rawShoulder.visibility.toFixed(2),
        el_v: rawElbow.visibility.toFixed(2),
        wr_v: rawWrist.visibility.toFixed(2),
        phase: this.phase,
      };
      return undefined;
    }

    // T2 — EMA: smoothing de x/y/z para shoulder/elbow/wrist.
    this.updateSmoothedKeypoints(rawShoulder, rawElbow, rawWrist);
    const sm = this.smoothedKeypoints!;

    // Ángulo computado sobre keypoints smoothed (consistente con Fix A/B).
    const angle = calc3DAngle(sm.elbow, sm.shoulder, sm.wrist);

    // Debug values — se leen en el HUD para calibrar thresholds en vivo.
    // Se setean acá (post visibility gate + smoothing) para que el ángulo
    // sea el mismo que usa la máquina de estados.
    //
    // Label 'ang_hombro' (no 'angle') para NO confundirse con el ángulo
    // del codo que muestra la regla codo_mal_angulado. Son ángulos
    // distintos sobre los mismos 3 KP con distinto vertex:
    //   - repDetector.angle: calc3DAngle(elbow, shoulder, wrist) →
    //     vertex=shoulder → ángulo de ROTACIÓN del brazo (lo que
    //     usamos para contar reps). Rango típico 15-60°.
    //   - regla codo_mal_angulado: calc3DAngle(shoulder, elbow, wrist)
    //     → vertex=elbow → ángulo interior del CODO (flexión). Rango
    //     típico 90-140° según extensión natural del paciente.
    //
    // 'zone' (START/PEAK/MID) hace obvio si el ángulo actual dispararía
    // la transición con los thresholds actuales — clave para calibrar.
    this.lastDebug = {
      ang_hombro: angle.toFixed(1),
      phase: this.phase,
      dwell: (now - this.phaseEnteredAt).toFixed(0),
      low: det.lowThreshold,
      high: det.highThreshold,
      dir: direction,
      zone: isInStartZone(angle)
        ? "START"
        : isInPeakZone(angle)
          ? "PEAK"
          : "MID",
    };

    // Track ángulo extremo de la rep en curso (min Y max — solo uno es
    // el "pico semántico" según direction, pero trackear ambos cuesta
    // nada y deja el evaluator simétrico).
    if (this.repInProgress) {
      this.repInProgress.angleMax = Math.max(
        this.repInProgress.angleMax,
        angle,
      );
      this.repInProgress.angleMin = Math.min(
        this.repInProgress.angleMin,
        angle,
      );
    }

    // Fix A/B — solo si visibility raw es ≥ threshold de validation por keypoint.
    if (
      this.repInProgress &&
      rawElbow.visibility >= MIN_VISIBILITY_VALIDATION_ELBOW &&
      rawWrist.visibility >= MIN_VISIBILITY_VALIDATION_WRIST
    ) {
      if (this.checkAndAbortIfMoved(sm, now)) {
        return undefined;
      }
    }

    const timeInPhase = now - this.phaseEnteredAt;

    switch (this.phase) {
      case "inicio":
        // T3 — Debounce: requerir N frames consecutivos FUERA de la start
        // zone (en la dirección esperada del movimiento).
        if (!isInStartZone(angle)) {
          this.framesOutsideStartZone++;
          if (
            this.framesOutsideStartZone >= PHASE_TRANSITION_DEBOUNCE_FRAMES
          ) {
            this.transitionTo("concentrica", now);
            this.framesOutsideStartZone = 0;
            this.repInProgress = {
              startedAt: now,
              angleMax: angle,
              angleMin: angle,
              violationsPerFrame: new Map(),
              totalFrames: 0,
              trunkAngleMin: 0,
              trunkAngleMax: 0,
              startPositions: {
                elbow: { x: sm.elbow.x, y: sm.elbow.y },
                wrist: { x: sm.wrist.x, y: sm.wrist.y },
              },
              angleAtStart: angle,
            };
            const t = this.safeTrunkAngle(kp);
            if (t !== null) {
              this.repInProgress.trunkAngleMin = t;
              this.repInProgress.trunkAngleMax = t;
            }
          }
        } else {
          this.framesOutsideStartZone = 0;
        }
        break;

      case "concentrica":
        // Avanzando hacia el pico (en la dirección esperada).
        if (isInPeakZone(angle)) {
          this.transitionTo("pico", now);
        } else if (isInStartZone(angle) && timeInPhase > det.dwellMs) {
          // Volvió a start zone sin completar — descartar la rep
          // (abort silencioso existente).
          this.repInProgress = null;
          this.transitionTo("inicio", now);
        }
        break;

      case "pico":
        // Sostuvo el pico por dwellMs?
        if (!isInPeakZone(angle)) {
          if (timeInPhase >= det.dwellMs) {
            // T4 — Peak prominence: amplitud real del movimiento debe ser
            // significativa, si no es un amago. Para increasing es
            // angleMax - angleAtStart (subió X grados). Para decreasing
            // es angleAtStart - angleMin (bajó X grados). Ambos son
            // positivos si hubo movimiento real.
            const prominence =
              direction === "decreasing"
                ? this.repInProgress!.angleAtStart -
                  this.repInProgress!.angleMin
                : this.repInProgress!.angleMax -
                  this.repInProgress!.angleAtStart;
            if (prominence < MIN_PEAK_PROMINENCE_DEGREES) {
              console.warn(
                "[Evaluator] rep aborted: peak prominence too low",
                {
                  angleMax: this.repInProgress!.angleMax,
                  angleAtStart: this.repInProgress!.angleAtStart,
                  prominence,
                },
              );
              this.repInProgress = null;
              this.transitionTo("inicio", now);
              this.framesOutsideStartZone = 0;
              return undefined;
            }
            this.transitionTo("excentrica", now);
          } else {
            this.transitionTo("concentrica", now); // no sostuvo, volvió
          }
        }
        break;

      case "excentrica":
        // Volviendo a start zone. Si llega y sostiene, cuenta como rep
        // completa. La dirección de "volver" depende de direction
        // (encapsulado en isInStartZone).
        if (isInStartZone(angle)) {
          if (timeInPhase >= det.dwellMs) {
            const completed = this.completeRep(now);
            this.transitionTo("inicio", now);
            return completed;
          }
        }
        break;
    }

    return undefined;
  }

  /** Actualiza el buffer de keypoints smoothed con EMA. Init lazy con raw
   *  values el primer frame válido. Visibility se mantiene raw (no smoothed). */
  private updateSmoothedKeypoints(
    shoulder: Keypoint,
    elbow: Keypoint,
    wrist: Keypoint,
  ): void {
    if (this.smoothedKeypoints === null) {
      this.smoothedKeypoints = {
        shoulder: { ...shoulder },
        elbow: { ...elbow },
        wrist: { ...wrist },
      };
      return;
    }
    const a = EMA_ALPHA;
    const sm = this.smoothedKeypoints;
    sm.shoulder.x = a * shoulder.x + (1 - a) * sm.shoulder.x;
    sm.shoulder.y = a * shoulder.y + (1 - a) * sm.shoulder.y;
    sm.shoulder.z = a * shoulder.z + (1 - a) * sm.shoulder.z;
    sm.shoulder.visibility = shoulder.visibility;
    sm.elbow.x = a * elbow.x + (1 - a) * sm.elbow.x;
    sm.elbow.y = a * elbow.y + (1 - a) * sm.elbow.y;
    sm.elbow.z = a * elbow.z + (1 - a) * sm.elbow.z;
    sm.elbow.visibility = elbow.visibility;
    sm.wrist.x = a * wrist.x + (1 - a) * sm.wrist.x;
    sm.wrist.y = a * wrist.y + (1 - a) * sm.wrist.y;
    sm.wrist.z = a * wrist.z + (1 - a) * sm.wrist.z;
    sm.wrist.visibility = wrist.visibility;
  }

  /** Si hay rep en curso y elbow/wrist se movieron más de los thresholds,
   *  aborta la rep y devuelve true. Smoothed positions son input. */
  private checkAndAbortIfMoved(
    sm: SmoothedKeypoints,
    now: number,
  ): boolean {
    if (!this.repInProgress) return false;
    const start = this.repInProgress.startPositions;

    // Fix A — codo estacionario (delta euclídeo xy)
    const elbowDx = sm.elbow.x - start.elbow.x;
    const elbowDy = sm.elbow.y - start.elbow.y;
    const elbowDelta = Math.sqrt(elbowDx * elbowDx + elbowDy * elbowDy);
    if (elbowDelta > ELBOW_STABILITY_THRESHOLD) {
      console.warn("[Evaluator] rep aborted: elbow moved", {
        dx: Number(elbowDx.toFixed(3)),
        dy: Number(elbowDy.toFixed(3)),
        delta: Number(elbowDelta.toFixed(3)),
      });
      this.repInProgress = null;
      this.transitionTo("inicio", now);
      this.framesOutsideStartZone = 0;
      return true;
    }

    // Fix B — wrist sin travel vertical
    const wristDy = Math.abs(sm.wrist.y - start.wrist.y);
    if (wristDy > WRIST_VERTICAL_THRESHOLD) {
      console.warn("[Evaluator] rep aborted: wrist vertical travel", {
        dy: Number(wristDy.toFixed(3)),
      });
      this.repInProgress = null;
      this.transitionTo("inicio", now);
      this.framesOutsideStartZone = 0;
      return true;
    }

    return false;
  }

  private transitionTo(phase: RepPhase, now: number): void {
    this.phase = phase;
    this.phaseEnteredAt = now;
  }

  private completeRep(now: number): RepResult {
    const r = this.repInProgress!;
    this.repCount++;

    // iter 4: filtrar las violaciones por porcentaje de frames afectados.
    // quality sigue computándose sobre TODAS las desviaciones (preserva
    // sensibilidad fina); el threshold del 25% solo afecta el array
    // `violations` y el flag binario `valid` que se derivan de él.
    const totalFrames = Math.max(r.totalFrames, 1);
    const uniqueViolations = Array.from(r.violationsPerFrame.entries())
      .filter(
        ([, frames]) => frames / totalFrames > VIOLATION_FRAME_PCT_THRESHOLD,
      )
      .map(([violationId]) => violationId);
    const violationPenalty = Array.from(r.violationsPerFrame.entries()).reduce(
      (acc, [, frames]) => acc + frames / totalFrames,
      0,
    );
    const quality = Math.max(0, Math.min(1, 1 - violationPenalty / 2));

    // Para reportar angleMax + prominence en RepResult, usamos el extremo
    // semántico según direction. Para increasing: max. Para decreasing:
    // min (que es el "pico" del movimiento). La field name `angleMax`
    // del RepResult se mantiene por backward compat — su semántica está
    // documentada en types.ts como "depende del evaluator".
    // Narrowing: este evaluator solo se instancia para schemas oscillating
    // (factory en exerciseSchemas.ts). El guard defensivo del union es
    // trivial pero necesario para satisfacer el compilador post-union nuevo
    // (Sprint 3 Bloque 4).
    const det = this.schema.repDetector;
    const direction =
      det && det.type === "oscillating_angle"
        ? (det.direction ?? "increasing")
        : "increasing";
    const peakAngle =
      direction === "decreasing" ? r.angleMin : r.angleMax;
    const prominence =
      direction === "decreasing"
        ? r.angleAtStart - r.angleMin
        : r.angleMax - r.angleAtStart;

    const rep: RepResult = {
      n: this.repCount,
      angleMax: peakAngle,
      durationMs: now - r.startedAt,
      quality: Number(quality.toFixed(2)),
      violations: uniqueViolations,
      angleAtStart: r.angleAtStart,
      prominence,
      valid: uniqueViolations.length === 0,
    };

    this.repInProgress = null;
    return rep;
  }

  getRepCount(): number {
    return this.repCount;
  }
}
